import Select from 'react-select';
import './DailyUtilisationReport.css';
import './CashTracking.css';
import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../context/UserContext';
import Swal from 'sweetalert2';
import { hasPrivilege } from '../utils/hasPrivilege';
import { FiChevronDown, FiRefreshCw } from 'react-icons/fi';
import { buildDiffPreview, fireAudit } from '../utils/auditClient';

const CashTracking = () => {
  const [academyFilter, setAcademyFilter] = useState(null);
  const today = new Date().toISOString().split('T')[0];
  const token = localStorage.getItem('auth-token');
  const [fromDateFilter, setFromDateFilter] = useState(today);
  const [toDateFilter, setToDateFilter] = useState(today);
  const [rowsPerPage, setrowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cashInHand, setCashInHand] = useState(null);
  const [data, setData] = useState([]);

  const [showSidePanel, setShowSidePanel] = useState(false);
  const { selectedCenter, userData } = useUser();
  const userId = userData?.userId;

  const [newExpense, setNewExpense] = useState({
    amount: '',
    purpose: '',
    comments: ''
  });

  const [academyOptions, setAcademyOptions] = useState([]);
  const [selectedAcademy, setSelectedAcademy] = useState(null);

  const baselineRef = useRef(null);
  const buildExpensePayload = (exp = newExpense) => ({
    givenBy: userId || null,
    amount: exp.amount !== '' ? Number(exp.amount) : 0,
    remarks: exp.purpose || '',
    academyId: selectedAcademy?.value || null,
  });

  useEffect(() => {
    if (showSidePanel) {
      baselineRef.current = buildExpensePayload({
        amount: '',
        purpose: '',
        comments: ''
      });
    }
  }, [showSidePanel, selectedAcademy, userId]);

  const fetchCashInHand = async () => {
    setLoading(true);
    let academyQuery = '';
    let dateQuery = '';

    if (userData?.userType === 'ACADEMY') {
      const allowed = selectedAcademy?.value
        ? [selectedAcademy.value]
        : userData.academyIds || [];

      if (!allowed.length) {
        setError('No academy access defined for this user.');
        setLoading(false);
        return;
      }
      academyQuery = `academyId=${allowed.join(',')}`;
    } else {
      academyQuery = selectedAcademy?.value ? `academyId=${selectedAcademy.value}` : '';
    }

    if (fromDateFilter) {
      dateQuery += `startDate=${fromDateFilter}`;
    }
    if (toDateFilter) {
      dateQuery += dateQuery ? `&endDate=${toDateFilter}` : `endDate=${toDateFilter}`;
    }

    let queryString = '';
    if (academyQuery && dateQuery) {
      queryString = `${academyQuery}&${dateQuery}`;
    } else if (academyQuery) {
      queryString = academyQuery;
    } else if (dateQuery) {
      queryString = dateQuery;
    }

    const apiUrl = `${process.env.REACT_APP_API_URL_CASH_AMOUNT}` + (queryString ? `?${queryString}` : '');
    try {
      const res = await fetch(apiUrl, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      const result = await res.json();

      if (result.success) {
        setCashInHand(result.data.TotalInHandCash);
        const formatted = (result.data.records || []).map(r => ({
          id: r.id,
          date: r.createdAt.split('T')[0],
          amount: `₹${r.amount}`,
          purpose: r.remarks,
          addedBy: `${r.givenByName}`,
        }));
        setData(formatted);
        setError('');
      } else {
        setError('Unable to load cash in hand.');
      }
    } catch (err) {
      setError('Network error.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCashInHand();
  }, [selectedAcademy, showSidePanel, userData, fromDateFilter, toDateFilter]);

  useEffect(() => {
    fetch(process.env.REACT_APP_API_URL_FEEDBACK_FETCH, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          let options = Object.entries(data.data.academyNameMap || {}).map(([id, name]) => ({
            value: parseInt(id, 10),
            label: name,
          }));

          if (userData?.userType === 'ACADEMY' && Array.isArray(userData.academyIds)) {
            options = options.filter(opt => userData.academyIds.includes(opt.value));
          }

          setAcademyOptions(options);

          if (selectedCenter) {
            const defaultAcademy = options.find(opt => opt.value === Number(selectedCenter));
            if (defaultAcademy) {
              setSelectedAcademy(defaultAcademy);
              setAcademyFilter(defaultAcademy);
            } else {
              console.error('No default academy found for selectedCenter:', selectedCenter);
            }
          }
        }
      })
      .catch(error => {
        console.error("Error fetching academy options:", error);
      });
  }, [selectedCenter, token, userData]);

  const filteredData = data.filter((entry) =>
    Object.values(entry).join(' ').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const [sortConfig, setSortConfig] = useState({
    key: 'coupon',
    direction: 'asc',
    numericKeys: ['orderAmount', 'couponDiscount']
  });
  const [currentPage, setCurrentPage] = useState(1);

  const sortData = (rows) => {
    const { key, direction } = sortConfig;
    return [...rows].sort((a, b) => {
      if (key === 'orderAmount' || key === 'couponDiscount') {
        const numA = parseFloat(a[key]) || 0;
        const numB = parseFloat(b[key]) || 0;
        return direction === 'asc' ? numA - numB : numB - numA;
      }
      const valueA = String(a[key] || '').toLowerCase();
      const valueB = String(b[key] || '').toLowerCase();

      if (valueA < valueB) return direction === 'asc' ? -1 : 1;
      if (valueA > valueB) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const sortedEnquiries = sortData(filteredData);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = sortedEnquiries.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(sortedEnquiries.length / rowsPerPage);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (event) => {
    setrowsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };
  const handlePageClick = (pageNumber) => setCurrentPage(pageNumber);
  const handleNextPage = () => currentPage < totalPages && setCurrentPage(p => p + 1);
  const handlePrevPage = () => currentPage > 1 && setCurrentPage(p => p - 1);

  const generatePageNumbers = () => {
    const pages = [];
    const totalPagesToShow = 5;

    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const renderPagination = () => {
    return generatePageNumbers().map((page, index) =>
      typeof page === 'number' ? (
        <button
          key={index}
          className={`page-number ${currentPage === page ? 'active' : ''}`}
          onClick={() => handlePageClick(page)}
        >
          {page}
        </button>
      ) : (
        <span key={index} className="pagination-dots">
          {page}
        </span>
      )
    );
  };

  const totalEntries = filteredData.length;
  const indexOfFirstEntry = totalEntries > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const indexOfLastEntry = totalEntries > 0 ? Math.min(currentPage * rowsPerPage, totalEntries) : 0;
  const sidePanelRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidePanelRef.current && !sidePanelRef.current.contains(event.target)) {
        setShowSidePanel(false);
      }
    };

    if (showSidePanel) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSidePanel]);

  useEffect(() => {
    if (!showSidePanel) {
      setNewExpense({ amount: '', purpose: '', comments: '' });
    }
  }, [showSidePanel]);

  const handleSubmit = async () => {
    if (!newExpense.amount) {
      Swal.fire({
        title: 'Missing Amount',
        text: 'Please enter the amount.',
        icon: 'warning',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 1500,
        allowOutsideClick: true,
      });
      return;
    }
    if (parseFloat(newExpense.amount) === 0) {
      Swal.fire({
        title: 'Invalid Amount',
        text: 'Amount cannot be zero.',
        icon: 'warning',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 1500,
        allowOutsideClick: true,
      });
      return;
    }
    if (!newExpense.purpose) {
      Swal.fire({
        title: 'Missing Purpose',
        text: 'Please enter the purpose or who it was given to.',
        icon: 'warning',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 1500,
        allowOutsideClick: true,
      });
      return;
    }
    if (!selectedAcademy?.value) {
      Swal.fire({
        title: 'Pick an academy',
        text: 'Please select an academy before adding an entry.',
        icon: 'warning',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 1600,
      });
      return;
    }

    const newPayload = buildExpensePayload(newExpense);
    const oldPayload = baselineRef.current || {};

    const { digest, html } = buildDiffPreview(oldPayload, newPayload);
    const confirm = await Swal.fire({
      title: 'Add this entry?',
      html: digest.length ? html : '<div style="text-align:left;font-size:13px">Submitting initial cash entry.</div>',
      showCancelButton: true,
      confirmButtonText: 'Add',
    });
    if (!confirm.isConfirmed) return;

    try {
      setLoading(true);
      const response = await fetch(process.env.REACT_APP_API_URL_CASH_SUBMIT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(newPayload),
      });

      const result = await response.json();
      if (result.success) {
        fireAudit({
          entity: 'cash_entry',
          affected_id: result?.data?.id || null,
          action: 'create',
          businessApi: process.env.REACT_APP_API_URL_CASH_SUBMIT,
          businessMethod: 'POST',
          oldPayload,
          newPayload,
          section: 'Centre Management',
          subSection: 'Cash Tracking',
          extra: { page: 'CashTracking' },
        });

        Swal.fire({
          title: 'Success!',
          text: 'Expense entry added successfully.',
          icon: 'success',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 1500,
          allowOutsideClick: true,
        });
        baselineRef.current = newPayload;

        setNewExpense({ amount: '', purpose: '', comments: '' });
        setShowSidePanel(false);
        fetchCashInHand();
      } else {
        Swal.fire({
          title: 'Error!',
          text: result?.success_message || 'Failed to add expense.',
          icon: 'error',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 1500,
          allowOutsideClick: true,
        });
      }
    } catch (error) {
      console.error("Error submitting expense:", error);
      Swal.fire({
        title: 'Error!',
        text: 'Something went wrong while submitting.',
        icon: 'error',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 1500,
        allowOutsideClick: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const headers = [
    { key: 'date', label: 'Date' },
    { key: 'amount', label: 'Amount' },
    { key: 'purpose', label: 'Purpose / Given To' },
    { key: 'addedBy', label: 'Added By' }
  ];

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  return (
    <div className="enrollment-container">
      <div className="sales-filters-login">
        <div className="floating-select">
          <Select
            className={`sales-filter-court-booking ${academyFilter ? "has-value" : ""}`}
            classNamePrefix="react-select"
            id="academyFilter"
            options={academyOptions}
            value={academyFilter}
            onChange={(value) => {
              setAcademyFilter(value);
              setSelectedAcademy(value);
            }}
            placeholder="All Academy"
            menuPortalTarget={document.body}
            styles={{
              placeholder: (provided) => ({ ...provided, color: 'black' }),
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
          {academyFilter && <label htmlFor="academyFilter">Academy</label>}
        </div>

        <div className="floating-select">
          <input
            type="date"
            className={`sales-filter-academy ${fromDateFilter ? "has-value" : ""}`}
            value={fromDateFilter}
            onChange={(e) => setFromDateFilter(e.target.value)}
            onClick={(e) => e.target.showPicker()}
            max={new Date().toISOString().split('T')[0]}
            id="fromDateFilter"
          />
          {fromDateFilter && <label htmlFor="fromDateFilter">From Date</label>}
        </div>

        <div className="floating-select">
          <input
            type="date"
            className={`sales-filter-academy ${toDateFilter ? "has-value" : ""}`}
            value={toDateFilter}
            onChange={(e) => setToDateFilter(e.target.value)}
            onClick={(e) => e.target.showPicker()}
            max={new Date().toISOString().split('T')[0]}
            id="toDateFilter"
          />
          {toDateFilter && <label htmlFor="toDateFilter">To Date</label>}
        </div>
        <div className="floating-select">
          <button className="tt-action-btn" onClick={fetchCashInHand}>
            <FiRefreshCw className={`refresh-icon ${loading ? 'spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="search-bar">
        <div className="input-container">
          <input
            type="text"
            id="searchInput"
            placeholder=""
            className={`search-input ${searchTerm ? "has-value" : ""}`}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <label htmlFor="searchInput" className="floating-label">🔍 Search</label>
        </div>
        {hasPrivilege(userData, 'CENTRE_MANAGEMENT', 'CASH_TRACKING_MODIFY') && (
          <button className="create-button-new" onClick={() => setShowSidePanel(true)}>
            <span className="create-button__icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="23" height="23" className="create-button__svg">
                <path d="M12 5v14M5 12h14" stroke="white" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <span className="create-button__label">Add Expenses/Deposit</span>
          </button>
        )}
      </div>

      <div className="items-and-dues">
        <select className="items-per-page-select" value={rowsPerPage} onChange={handleItemsPerPageChange}>
          <option value={10}>10 items per page</option>
          <option value={50}>50 items per page</option>
          <option value={75}>75 items per page</option>
          <option value={100}>100 items per page</option>
        </select>

        <div className="pagination-selector">
          <div className="button-group">
            <div className="group-label">Items per page:</div>
            <div className="button-options">
              {[10, 50, 75, 100].map((option) => (
                <button
                  key={option}
                  onClick={() => handleItemsPerPageChange({ target: { value: option } })}
                  className={`page-button ${rowsPerPage === option ? 'active' : ''}`}
                >
                  <span className="button-text">{option}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="total-dues">
          <strong>Cash in Hand: {cashInHand !== null ? `₹${cashInHand.toLocaleString()}` : 'Loading...'}</strong>
        </div>
      </div>

      {loading && (
        <div className="premium-loader">
          <div className="progress-track"><div className="progress-bar"></div></div>
          <div className="status-text">
            <span>L</span><span>O</span><span>A</span><span>D</span><span>I</span><span>N</span><span>G</span>
          </div>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}
      {!loading && !error && filteredData.length === 0 && (
        <div className="error-message">No Expenses record available</div>
      )}

      <div className="tt-table-container">
        <table className="tt-table">
          <thead>
            <tr>
              {[
                { key: 'date', label: 'Date' },
                { key: 'amount', label: 'Amount' },
                { key: 'purpose', label: 'Purpose / Given To' },
                { key: 'addedBy', label: 'Added By' }
              ].map(({ key, label }) => (
                <th key={key} className="challenges-column" onClick={() => handleSort(key)}>
                  <div className="th-content">
                    <span>{label}</span>
                    {sortConfig.key === key ? (
                      sortConfig.direction === 'asc' ? (
                        <FiChevronDown className="sort-icon active" />
                      ) : (
                        <FiChevronDown className="sort-icon active desc" />
                      )
                    ) : (
                      <FiChevronDown className="sort-icon" />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentRows.map((row) => (
              <tr key={row.id}>
                <td>{row.date}</td>
                <td>{row.amount}</td>
                <td>{row.purpose}</td>
                <td>{row.addedBy}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <div className="showing-entries">
          Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
        </div>
        <button className="prev-button" onClick={handlePrevPage} disabled={currentPage === 1}>Previous</button>
        {renderPagination()}
        <button className="next-button" onClick={handleNextPage} disabled={currentPage === totalPages}>Next</button>
      </div>

      {showSidePanel && (
        <div className="side-panel-tracking">
          <div className="side-panel-content" ref={sidePanelRef}>
            <h3>Add New Expense / Deposit</h3>

            <div className="floating-select-form" style={{ width: '95%' }}>
              <input
                type="text"
                name="amount"
                value={newExpense.amount}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^-?\d*\.?\d*$/.test(value)) {
                    setNewExpense({ ...newExpense, amount: value });
                  }
                }}
                className={`walkin-form ${newExpense.amount ? 'has-value' : ''}`}
                placeholder="Amount"
                maxLength={10}
                required
              />
              {newExpense.amount && <label htmlFor="amount">Amount</label>}
            </div>

            <div className="floating-select-form" style={{ width: '95%' }}>
              <input
                type="text"
                name="purpose"
                value={newExpense.purpose}
                onChange={(e) => setNewExpense({ ...newExpense, purpose: e.target.value })}
                className={`walkin-form ${newExpense.purpose ? 'has-value' : ''}`}
                placeholder="Purpose / Given to"
                required
              />
              {newExpense.purpose && <label htmlFor="purpose">Purpose / Give to</label>}
            </div>

            <button onClick={handleSubmit}>Submit</button>
            <button onClick={() => setShowSidePanel(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CashTracking;
